import React, { useState } from "react";
import NonPublicSeo from "../components/NonPublicSeo";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { storePartnerIntake } from "../util/http";
import { uploadPartnerIntakeFile } from "../lib/storage";
import "../styles/homepage-redesign.css";
import symLogo from "../assets/SYM_email_banner_speechBubble.svg";

const PARTNER_ID = "restaurant-onboarding";
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function fileIsValid(file) {
  if (!file) return false;
  if (!ALLOWED_MIME_TYPES.has(String(file.type || "").toLowerCase())) return false;
  return Number(file.size || 0) > 0 && Number(file.size || 0) <= MAX_FILE_SIZE_BYTES;
}

function intakeClientId() {
  const random = Math.random().toString(16).slice(2, 10);
  return `ro-${Date.now()}-${random}`;
}

function hasValidationErrors(validationErrors) {
  const topLevelErrors = Object.entries(validationErrors || {}).some(([key, value]) => key !== "restaurants" && Boolean(value));
  const restaurantErrors = Array.isArray(validationErrors?.restaurants)
    ? validationErrors.restaurants.some((restaurantError) => restaurantError && Object.keys(restaurantError).length > 0)
    : false;
  return topLevelErrors || restaurantErrors;
}

function describeSubmitError(err) {
  const status = err?.response?.status;
  if (status === 429) {
    return "Too many submissions from this connection. Please wait a few minutes and try again.";
  }
  const responseData = err?.response?.data;
  const rawMessage = typeof responseData === "string"
    ? responseData.trim()
    : typeof responseData?.error === "string"
      ? responseData.error.trim()
      : String(err?.message || "").trim();
  if (rawMessage === "Invalid payload" || rawMessage === "Bad Request") {
    return "There was a problem submitting your form. Please check that all required files have been added and all fields are filled in, then try again. If the problem continues, contact us at support@speakyourmenu.com.";
  }
  return rawMessage || "We could not submit your form right now. Please try again, or contact us at support@speakyourmenu.com if the issue continues.";
}

function createRestaurantState() {
  return {
    restaurantName: "",
    restaurantAddress: "",
    cuisineType: "",
    foodMenuFiles: [],
    wineMenuFiles: [],
    employeeRosterFiles: [],
    supportingDocuments: [],
  };
}

function classifyFile(file) {
  if (!file) return null;
  if (!ALLOWED_MIME_TYPES.has(String(file.type || "").toLowerCase())) {
    return { valid: false, reason: "type", name: file.name };
  }
  if (Number(file.size || 0) > MAX_FILE_SIZE_BYTES) {
    return { valid: false, reason: "size", name: file.name };
  }
  return { valid: true, name: file.name };
}

function FileDropInput({ label, multiple = false, files, onFilesSelected, description, note, error, inputId }) {
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);

  function processIncoming(incoming) {
    if (!incoming.length) {
      setRejectedFiles([]);
      onFilesSelected(multiple ? [] : null);
      return;
    }

    if (multiple) {
      const valid = [];
      const rejected = [];
      for (const file of incoming) {
        const result = classifyFile(file);
        if (result?.valid) {
          valid.push(file);
        } else if (result) {
          rejected.push(result);
        }
      }
      setRejectedFiles(rejected);
      onFilesSelected(valid);
    } else {
      const file = incoming[0];
      const result = classifyFile(file);
      if (result?.valid) {
        setRejectedFiles([]);
        onFilesSelected(file);
      } else {
        setRejectedFiles(result ? [result] : []);
        onFilesSelected(null);
      }
    }
  }

  function handleDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    processIncoming(Array.from(event.dataTransfer?.files || []));
  }

  function handleChange(event) {
    processIncoming(Array.from(event.target.files || []));
    event.target.value = "";
  }

  const selectedList = multiple
    ? Array.isArray(files)
      ? files.map((file) => file?.name).filter(Boolean)
      : []
    : files?.name
      ? [files.name]
      : [];

  const hasValidFiles = selectedList.length > 0;
  const hasRejected = rejectedFiles.length > 0;

  return (
    <div>
      <label className="form-label fw-semibold" style={{ marginBottom: "0.3rem" }} htmlFor={inputId}>{label}</label>
      {description ? (
        <p style={{ margin: "0 0 0.45rem", fontSize: "0.825rem", color: "var(--bs-secondary-color)", lineHeight: 1.5 }}>
          {description}
        </p>
      ) : null}
      <div
        style={{
          border: `1.5px ${isDragging ? "solid var(--homepage-accent-a)" : "dashed color-mix(in srgb, var(--homepage-accent-a) 35%, transparent)"}`,
          borderRadius: "8px",
          padding: "0.75rem",
          background: isDragging ? "color-mix(in srgb, var(--color-wine-white) 60%, white)" : "color-mix(in srgb, var(--color-wine-white) 30%, white)",
          transition: "border-color 120ms ease, background 120ms ease",
        }}
        onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          id={inputId}
          type="file"
          className="form-control form-control-sm"
          accept=".jpg,.jpeg,.png,.pdf,.csv,.xls,.xlsx"
          multiple={multiple}
          onChange={handleChange}
        />
        <div style={{ marginTop: "0.4rem", fontSize: "0.8125rem", color: "var(--bs-secondary-color)" }}>
          {multiple ? "Drag and drop files here, or choose files above." : "Drag and drop a file here, or choose a file above."}
        </div>
      </div>
      <div style={{ marginTop: "0.3rem", fontSize: "0.73rem", color: "#9ca3af", letterSpacing: "0.01em" }}>
        Accepted: JPG, PNG, PDF, CSV, XLS, XLSX · Max 20MB
      </div>
      {note ? (
        <p style={{ margin: "0.3rem 0 0", fontSize: "0.8125rem", color: "var(--bs-secondary-color)", fontStyle: "italic", lineHeight: 1.55 }}>
          {note}
        </p>
      ) : null}
      {hasValidFiles && !hasRejected ? (
        <div style={{
          marginTop: "0.55rem",
          padding: "0.5rem 0.75rem",
          borderRadius: "8px",
          background: "color-mix(in srgb, #22c55e 10%, white)",
          border: "1px solid color-mix(in srgb, #22c55e 35%, transparent)",
          display: "flex",
          alignItems: "flex-start",
          gap: "0.5rem",
        }}>
          <span style={{ color: "#16a34a", fontSize: "1rem", lineHeight: 1.3, flexShrink: 0, fontWeight: 700 }}>✓</span>
          <div>
            <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "#16a34a", letterSpacing: "0.02em", textTransform: "uppercase" }}>
              {multiple && selectedList.length > 1 ? `${selectedList.length} files added` : "File added"}
            </span>
            {selectedList.map((name) => (
              <div key={name} style={{ fontSize: "0.82rem", color: "#15803d", lineHeight: 1.45, wordBreak: "break-all", marginTop: "0.1rem" }}>{name}</div>
            ))}
          </div>
        </div>
      ) : null}
      {hasRejected ? (
        <div style={{ marginTop: "0.55rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          {rejectedFiles.map((rejected) => (
            <div key={rejected.name} style={{
              padding: "0.5rem 0.75rem",
              borderRadius: "8px",
              background: "color-mix(in srgb, #ef4444 8%, white)",
              border: "1px solid color-mix(in srgb, #ef4444 30%, transparent)",
              display: "flex",
              alignItems: "flex-start",
              gap: "0.5rem",
            }}>
              <span style={{ color: "#dc2626", fontSize: "1rem", lineHeight: 1.3, flexShrink: 0, fontWeight: 700 }}>✕</span>
              <div>
                <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: "#dc2626", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                  File not added
                </span>
                <div style={{ fontSize: "0.82rem", color: "#991b1b", lineHeight: 1.45, wordBreak: "break-all", marginTop: "0.1rem" }}>{rejected.name}</div>
                <div style={{ fontSize: "0.8rem", color: "#b91c1c", lineHeight: 1.45, marginTop: "0.2rem" }}>
                  {rejected.reason === "size"
                    ? "This file is larger than the 20MB limit. Please upload a file under 20MB."
                    : "This file type is not supported for this upload. Accepted file types: JPG, PNG, PDF, CSV, XLS, XLSX."}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {error ? <div className="text-danger small mt-1">{error}</div> : null}
    </div>
  );
}

export default function RestaurantOnboardingPage() {
  const [values, setValues] = useState({
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    restaurantCount: 1,
    website: "",
  });
  const [restaurants, setRestaurants] = useState([createRestaurantState()]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function onChange(event) {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  function incrementCount() {
    setValues((prev) => {
      if (prev.restaurantCount >= 6) return prev;
      return { ...prev, restaurantCount: prev.restaurantCount + 1 };
    });
    setRestaurants((r) => {
      if (r.length >= 6) return r;
      return [...r, createRestaurantState()];
    });
  }

  function decrementCount() {
    setValues((prev) => {
      if (prev.restaurantCount <= 1) return prev;
      return { ...prev, restaurantCount: prev.restaurantCount - 1 };
    });
    setRestaurants((r) => {
      if (r.length <= 1) return r;
      return r.slice(0, r.length - 1);
    });
  }

  function updateRestaurant(index, field, value) {
    setRestaurants((prev) => prev.map((restaurant, currentIndex) => (
      currentIndex === index ? { ...restaurant, [field]: value } : restaurant
    )));

    setErrors((prev) => {
      const nextRestaurants = Array.isArray(prev.restaurants) ? [...prev.restaurants] : [];
      if (nextRestaurants[index] && nextRestaurants[index][field]) {
        nextRestaurants[index] = { ...nextRestaurants[index], [field]: "" };
      }
      return { ...prev, restaurants: nextRestaurants };
    });
  }

  function validate() {
    const next = { restaurants: [] };
    if (!values.contactName.trim()) next.contactName = "Contact name is required.";
    if (!isValidEmail(values.contactEmail)) next.contactEmail = "Valid contact email is required.";
    if (!values.contactPhone.trim()) next.contactPhone = "Contact phone is required.";
    const restaurantCount = values.restaurantCount;
    if (restaurants.length !== restaurantCount) {
      next.restaurantCount = "Restaurant sections are not aligned with the restaurant count yet.";
    }

    restaurants.forEach((restaurant, index) => {
      const restaurantErrors = {};
      if (!restaurant.restaurantName.trim()) restaurantErrors.restaurantName = "Restaurant name is required.";
      if (!restaurant.restaurantAddress.trim()) restaurantErrors.restaurantAddress = "Restaurant address is required.";
      if (!restaurant.cuisineType.trim()) restaurantErrors.cuisineType = "Cuisine type is required.";
      if (restaurant.foodMenuFiles.length === 0) restaurantErrors.foodMenuFiles = "Upload at least one food menu file.";
      else if (restaurant.foodMenuFiles.some((f) => !fileIsValid(f))) restaurantErrors.foodMenuFiles = "One or more food menu files are invalid.";
      if (restaurant.wineMenuFiles.length > 0 && restaurant.wineMenuFiles.some((f) => !fileIsValid(f))) restaurantErrors.wineMenuFiles = "One or more wine menu files are invalid.";
      if (restaurant.employeeRosterFiles.length > 0 && restaurant.employeeRosterFiles.some((f) => !fileIsValid(f))) restaurantErrors.employeeRosterFiles = "One or more employee roster files are invalid.";
      const invalidSupporting = restaurant.supportingDocuments.find((file) => !fileIsValid(file));
      if (invalidSupporting) {
        restaurantErrors.supportingDocuments = "One or more supporting documents are invalid.";
      }
      next.restaurants[index] = restaurantErrors;
    });

    return next;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationErrors = validate();
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const clientId = intakeClientId();
      const uploadedRestaurants = await Promise.all(
        restaurants.map(async (restaurant, index) => {
          const [foodMenuMeta, wineMenuMeta, employeeRosterMeta, supportingMeta] = await Promise.all([
            Promise.all(restaurant.foodMenuFiles.map((file, fileIndex) =>
              uploadPartnerIntakeFile({ partnerId: PARTNER_ID, intakeClientId: clientId, restaurantIndex: index, category: `food-menu-${fileIndex + 1}`, file })
            )),
            restaurant.wineMenuFiles.length > 0
              ? Promise.all(restaurant.wineMenuFiles.map((file, fileIndex) =>
                  uploadPartnerIntakeFile({ partnerId: PARTNER_ID, intakeClientId: clientId, restaurantIndex: index, category: `wine-menu-${fileIndex + 1}`, file })
                ))
              : Promise.resolve([]),
            Promise.all(restaurant.employeeRosterFiles.map((file, fileIndex) =>
              uploadPartnerIntakeFile({ partnerId: PARTNER_ID, intakeClientId: clientId, restaurantIndex: index, category: `employee-roster-${fileIndex + 1}`, file })
            )),
            Promise.all(
              restaurant.supportingDocuments.map((file, supportingIndex) =>
                uploadPartnerIntakeFile({
                  partnerId: PARTNER_ID,
                  intakeClientId: clientId,
                  restaurantIndex: index,
                  category: `supporting-${supportingIndex + 1}`,
                  file,
                })
              )
            ),
          ]);

          return {
            restaurantIndex: index,
            restaurantName: restaurant.restaurantName.trim(),
            restaurantAddress: restaurant.restaurantAddress.trim(),
            cuisineType: restaurant.cuisineType.trim(),
            files: {
              foodMenuFiles: foodMenuMeta,
              wineMenuFiles: wineMenuMeta,
              employeeRosterFiles: employeeRosterMeta,
              supportingDocuments: supportingMeta,
            },
          };
        })
      );

      await storePartnerIntake({
        partnerId: PARTNER_ID,
        contactName: values.contactName.trim(),
        contactEmail: values.contactEmail.trim().toLowerCase(),
        contactPhone: values.contactPhone.trim(),
        restaurantCount: uploadedRestaurants.length,
        restaurants: uploadedRestaurants,
        landingPath:
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search || ""}`
            : "/restaurant-onboarding",
        referrer: typeof document !== "undefined" ? document.referrer || "" : "",
        website: values.website,
      });

      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: describeSubmitError(err) });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="homepage-redesign">
      <NonPublicSeo
        title="Restaurant Onboarding | Speak Your Menu"
        description="Submit your restaurant details to get started with Speak Your Menu."
      />
      <Nav />
      <main id="main-content" tabIndex="-1">
        <section className="homepage-hero-section homepage-section-alt" style={{ position: "relative", paddingTop: "3rem", paddingBottom: "4rem" }}>
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-shell" style={{ position: "relative", zIndex: 1 }}>

            <div className="homepage-hero-grid" style={{ alignItems: "start", gap: "2.5rem" }}>

              {/* Left column */}
              <div className="homepage-hero-copy">
                <span className="homepage-label-chip">Restaurant Onboarding</span>
                <h1
                  className="homepage-section-title mt-3"
                  style={{ color: "var(--color-wine-red)", maxWidth: "none" }}
                >
                  Let&#8217;s get your restaurant set up in Speak Your Menu
                </h1>
                <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "36rem" }}>
                  <p className="homepage-section-body" style={{ margin: 0 }}>
                    Use this page to send us the materials we need to build the first version of your Speak Your Menu account. Once we receive your menu and staff information, we&#8217;ll prepare your restaurant setup and follow up with next steps.
                  </p>
                  <p className="homepage-section-body" style={{ margin: 0 }}>
                    You do not need perfect files. PDFs, photos, spreadsheets, website menu links, or documents are all fine. Send what you have, and we&#8217;ll organize it from there.
                  </p>
                  <p className="homepage-section-body" style={{ margin: 0 }}>
                    If you are setting up more than one restaurant, add a separate section for each location so the menu files and staff details stay organized.
                  </p>
                </div>

                {/* What happens after you submit */}
                <div style={{ marginTop: "1.5rem", maxWidth: "36rem" }}>
                  <p style={{ margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 700, color: "var(--homepage-accent-a)", letterSpacing: "0.01em" }}>
                    What happens after you submit
                  </p>
                  <p className="homepage-section-body" style={{ margin: "0 0 0.5rem" }}>
                    We&#8217;ll use your materials to create your initial training setup. Your team will be able to learn from your actual menu, including dishes, ingredients, wine details, and the kinds of information staff need to speak confidently with guests.
                  </p>
                  <p className="homepage-section-body" style={{ margin: 0 }}>
                    Once your account is ready, we&#8217;ll send you a link to schedule a walkthrough so we can show you the dashboard, lessons, and staff invitation flow.
                  </p>
                </div>

                {/* Readiness checklist */}
                <div style={{
                  marginTop: "1.5rem",
                  maxWidth: "36rem",
                  padding: "1.1rem 1.25rem",
                  borderRadius: "12px",
                  background: "color-mix(in srgb, var(--color-wine-white) 55%, white)",
                  border: "1px solid color-mix(in srgb, var(--homepage-accent-a) 18%, transparent)",
                }}>
                  <p style={{ margin: "0 0 0.65rem", fontSize: "1rem", fontWeight: 700, color: "var(--homepage-accent-a)", letterSpacing: "0.01em" }}>
                    Helpful materials to send
                  </p>
                  <ul style={{ margin: "0 0 0.75rem", paddingLeft: "1.15rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    {[
                      "Current food menu",
                      "Wine menu, if applicable",
                      "Staff names and email addresses, if you want us to help invite your team",
                      "Restaurant address and cuisine type",
                      "Any training notes, allergy details, ingredient lists, or menu documents you already use",
                    ].map((item) => (
                      <li key={item} style={{ fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p style={{ margin: 0, fontSize: "0.825rem", color: "var(--bs-secondary-color)", lineHeight: 1.55, fontStyle: "italic" }}>
                    Do not worry if you do not have everything ready. Send what you have now, and we can help fill in the gaps during onboarding.
                  </p>
                </div>

                <div style={{ marginTop: "1.75rem" }}>
                  <button
                    type="button"
                    className="homepage-primary-cta"
                    onClick={() => {
                      const formEl = document.getElementById("intake-form");
                      if (!formEl) return;
                      formEl.scrollIntoView({ behavior: "smooth", block: "start" });
                      setTimeout(() => {
                        const firstInput = formEl.querySelector("input[name='contactName']");
                        if (firstInput) firstInput.focus();
                      }, 600);
                    }}
                  >
                    Start Onboarding
                  </button>
                </div>
              </div>

              {/* Right column — info card */}
              <div>
                <div
                  className="homepage-surface-card"
                  style={{ padding: "1.75rem 1.75rem 1.5rem", borderRadius: "20px" }}
                >
                  {/* SYM logo */}
                  <div style={{ marginBottom: "1.25rem" }}>
                    <img
                      src={symLogo}
                      alt="Speak Your Menu"
                      style={{ height: 44, width: "auto", objectFit: "contain" }}
                    />
                  </div>

                  <p style={{ margin: "0 0 1.25rem", fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.65 }}>
                    If you already created your Speak Your Menu account, this page helps us turn your restaurant information into a working training setup for your team.
                  </p>

                  <hr style={{ margin: "0 0 1.1rem", borderColor: "var(--homepage-border)" }} />

                  {/* What happens next */}
                  <p style={{ margin: "0 0 0.65rem", fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--homepage-accent-a)" }}>
                    What Happens Next
                  </p>
                  <div style={{ margin: "0 0 1.25rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {[
                      "You send us your menu, staff information, and any helpful notes",
                      "We review your materials and prepare your initial account setup",
                      "We follow up when your account is ready",
                      "You schedule a walkthrough so we can show you how everything works",
                      "Your team starts training in Speak Your Menu",
                    ].map((step, i) => (
                      <div key={step} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: "color-mix(in srgb, var(--homepage-accent-a) 12%, white)",
                          border: "1.5px solid color-mix(in srgb, var(--homepage-accent-a) 30%, transparent)",
                          color: "var(--homepage-accent-a)",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1,
                          marginTop: "0.1rem",
                        }}>{i + 1}</span>
                        <span style={{ fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                          {step}
                        </span>
                      </div>
                    ))}
                  </div>

                  <hr style={{ margin: "0 0 1.1rem", borderColor: "var(--homepage-border)" }} />

                  {/* Secure submission */}
                  <p style={{ margin: "0 0 0.5rem", fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--homepage-accent-a)" }}>
                    Secure Submission
                  </p>
                  <p style={{ margin: "0 0 1.25rem", fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.65 }}>
                    Your files and contact information are used only to set up and support your Speak Your Menu account.
                  </p>

                  {/* Questions */}
                  <div style={{
                    marginTop: "0.5rem",
                    background: "color-mix(in srgb, var(--color-wine-white) 65%, white)",
                    border: "1px solid color-mix(in srgb, var(--homepage-accent-a) 16%, transparent)",
                    borderRadius: "12px",
                    padding: "0.95rem 1rem",
                  }}>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                      <span style={{ fontSize: "1.1rem", lineHeight: 1.3, flexShrink: 0, marginTop: "0.05rem" }}>✉</span>
                      <div>
                        <p style={{ margin: "0 0 0.3rem", fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--homepage-accent-a)" }}>
                          Questions Before You Submit?
                        </p>
                        <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.65 }}>
                          Questions before you submit? Email us at{" "}
                          <a
                            href="mailto:support@speakyourmenu.com"
                            style={{ color: "var(--homepage-accent-a)", fontWeight: 700, textDecoration: "underline", fontSize: "0.95rem" }}
                          >
                            support@speakyourmenu.com
                          </a>
                          {" "}or reply directly to the message that brought you here.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section id="intake-form" className="homepage-section">
          <div className="homepage-shell">
            <div style={{ marginBottom: "2rem" }}>
              <h2 style={{ fontSize: "clamp(1.4rem, 4vw, 1.9rem)", fontWeight: 700, color: "var(--color-wine-red)", margin: "0 0 0.5rem" }}>
                Send your onboarding materials
              </h2>
              <p style={{ margin: 0, fontSize: "0.95rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                Enter your contact information, then complete one section for each restaurant location you want us to set up. Upload whatever materials you have available, and we&#8217;ll use them to prepare your account before your walkthrough.
              </p>
            </div>

          {submitted ? (
            <div
              className="card"
              role="status"
              style={{
                border: "1px solid color-mix(in srgb, #22c55e 35%, transparent)",
                borderRadius: "16px",
                boxShadow: "0 4px 24px rgba(17,17,17,0.07)",
                background: "var(--bs-body-bg)",
                overflow: "hidden",
              }}
            >
              <div style={{ background: "color-mix(in srgb, #22c55e 14%, white)", padding: "1.5rem 1.75rem", borderBottom: "1px solid color-mix(in srgb, #22c55e 25%, transparent)", display: "flex", alignItems: "flex-start", gap: "0.85rem" }}>
                <span style={{ fontSize: "1.5rem", lineHeight: 1.2, color: "#16a34a", fontWeight: 700, flexShrink: 0, marginTop: "0.1rem" }}>✓</span>
                <div>
                  <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#15803d", lineHeight: 1.35 }}>
                    Your onboarding form was submitted successfully.
                  </p>
                  <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem", color: "#166534", lineHeight: 1.6 }}>
                    Thank you for making menu training a priority for your team. You&#8217;re one step closer to getting your restaurant set up in Speak Your Menu.
                  </p>
                  <p style={{ margin: "0.4rem 0 0", fontSize: "0.875rem", color: "#166534", lineHeight: 1.5 }}>
                    We&#8217;ve received your restaurant information and uploaded files.
                  </p>
                </div>
              </div>
              <div style={{ padding: "1.5rem 1.75rem" }}>
                <p style={{ margin: "0 0 1rem", fontSize: "0.875rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                  Please allow approximately 3 to 5 business days for account setup, depending on submission volume.
                </p>
                <p style={{ margin: "0 0 0.65rem", fontSize: "0.8125rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--homepage-accent-a)" }}>
                  What happens next
                </p>
                <ol style={{ margin: "0 0 1.5rem", paddingLeft: "1.2rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {[
                    "We\u2019ll review your submission and files.",
                    "You\u2019ll receive a confirmation email from us shortly.",
                    "We\u2019ll prepare your account and let you know when it\u2019s ready for the next step.",
                    "We\u2019ll schedule a Zoom walkthrough to help you get started in Speak Your Menu.",
                  ].map((step) => (
                    <li key={step} style={{ fontSize: "0.9rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                      {step}
                    </li>
                  ))}
                </ol>
                <hr style={{ margin: "0 0 1.1rem", borderColor: "var(--homepage-border)" }} />
                <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                  If you have any questions in the meantime, we&#8217;re happy to help at{" "}
                  <a
                    href="mailto:support@speakyourmenu.com"
                    style={{ color: "var(--homepage-accent-a)", fontWeight: 600, textDecoration: "underline" }}
                  >
                    support@speakyourmenu.com
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <form className="card" onSubmit={handleSubmit} noValidate style={{ border: "1px solid color-mix(in srgb, var(--homepage-accent-a) 20%, transparent)", borderRadius: "16px", boxShadow: "0 4px 24px rgba(17,17,17,0.07)", background: "var(--bs-body-bg)" }}>
              <div className="card-body p-4">
                <div style={{ marginBottom: "1rem", padding: "0.85rem 1.1rem", borderRadius: "10px", background: "color-mix(in srgb, var(--color-wine-white) 55%, white)", border: "1px solid color-mix(in srgb, var(--homepage-accent-a) 14%, transparent)" }}>
                  <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                    Upload whatever you have available. Photos of printed menus are fine as long as the text is readable. If a file is missing or something needs clarification, we&#8217;ll follow up.
                  </p>
                </div>
                <p style={{ margin: "0 0 1.5rem", fontSize: "0.8rem", color: "#9ca3af", lineHeight: 1.55 }}>
                  Your files are used only to prepare and support your Speak Your Menu setup.
                </p>
                <input
                  type="hidden"
                  name="website"
                  value={values.website}
                />

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Contact Name</label>
                    <input className="form-control" name="contactName" value={values.contactName} onChange={onChange} />
                    {errors.contactName ? <div className="text-danger small mt-1">{errors.contactName}</div> : null}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact Email</label>
                    <input className="form-control" name="contactEmail" value={values.contactEmail} onChange={onChange} inputMode="email" />
                    <div className="form-text">Use the best email for onboarding follow-up and scheduling.</div>
                    {errors.contactEmail ? <div className="text-danger small mt-1">{errors.contactEmail}</div> : null}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact Phone</label>
                    <input className="form-control" name="contactPhone" value={values.contactPhone} onChange={onChange} />
                    {errors.contactPhone ? <div className="text-danger small mt-1">{errors.contactPhone}</div> : null}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label d-block">How many restaurant locations are you setting up?</label>
                    <div className="d-flex align-items-center gap-3 mt-1">
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40, fontSize: "1.2rem", flexShrink: 0 }}
                        onClick={decrementCount}
                        disabled={values.restaurantCount <= 1}
                        aria-label="Decrease restaurant count"
                      >
                        −
                      </button>
                      <span
                        className="fw-bold text-center"
                        style={{ minWidth: 32, fontSize: "1.4rem", color: "var(--color-wine-red)" }}
                        aria-live="polite"
                        aria-label={`${values.restaurantCount} restaurant${values.restaurantCount !== 1 ? "s" : ""}`}
                      >
                        {values.restaurantCount}
                      </span>
                      <button
                        type="button"
                        className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40, fontSize: "1.2rem", flexShrink: 0 }}
                        onClick={incrementCount}
                        disabled={values.restaurantCount >= 6}
                        aria-label="Increase restaurant count"
                      >
                        +
                      </button>
                    </div>
                    <div className="form-text mt-2">A separate intake section will be created for each restaurant so files and details stay clearly separated.</div>
                    {errors.restaurantCount ? <div className="text-danger small mt-1">{errors.restaurantCount}</div> : null}
                  </div>
                </div>

                {restaurants.map((restaurant, index) => {
                  const restaurantErrors = Array.isArray(errors.restaurants) ? errors.restaurants[index] || {} : {};
                  return (
                    <div key={`restaurant-section-${index}`} className="homepage-surface-card p-4 mt-4" style={{ border: "1px solid color-mix(in srgb, var(--homepage-accent-a) 16%, transparent)" }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="h5 fw-bold mb-0" style={{ color: "var(--color-wine-red)" }}>Restaurant {index + 1}</h2>
                        <span className="badge text-bg-light">Upload files for this restaurant</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label" htmlFor={`ro-restaurant-name-${index}`}>Restaurant Name</label>
                          <input id={`ro-restaurant-name-${index}`} className="form-control" value={restaurant.restaurantName} onChange={(event) => updateRestaurant(index, "restaurantName", event.target.value)} />
                          {restaurantErrors.restaurantName ? <div className="text-danger small mt-1">{restaurantErrors.restaurantName}</div> : null}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor={`ro-restaurant-cuisine-${index}`}>Cuisine Type</label>
                          <input id={`ro-restaurant-cuisine-${index}`} className="form-control" value={restaurant.cuisineType} onChange={(event) => updateRestaurant(index, "cuisineType", event.target.value)} />
                          {restaurantErrors.cuisineType ? <div className="text-danger small mt-1">{restaurantErrors.cuisineType}</div> : null}
                        </div>
                        <div className="col-12">
                          <label className="form-label" htmlFor={`ro-restaurant-address-${index}`}>Restaurant Address</label>
                          <input id={`ro-restaurant-address-${index}`} className="form-control" value={restaurant.restaurantAddress} onChange={(event) => updateRestaurant(index, "restaurantAddress", event.target.value)} />
                          {restaurantErrors.restaurantAddress ? <div className="text-danger small mt-1">{restaurantErrors.restaurantAddress}</div> : null}
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-md-6">
                          <FileDropInput
                            label="Food menu"
                            multiple
                            files={restaurant.foodMenuFiles}
                            onFilesSelected={(files) => updateRestaurant(index, "foodMenuFiles", files)}
                            error={restaurantErrors.foodMenuFiles}
                            inputId={`ro-food-menu-${index}`}
                          />
                        </div>
                        <div className="col-md-6">
                          <FileDropInput
                            label="Wine menu, if applicable"
                            multiple
                            files={restaurant.wineMenuFiles}
                            onFilesSelected={(files) => updateRestaurant(index, "wineMenuFiles", files)}
                            error={restaurantErrors.wineMenuFiles}
                            inputId={`ro-wine-menu-${index}`}
                          />
                        </div>
                        <div className="col-md-6">
                          <FileDropInput
                            label="Staff list"
                            multiple
                            files={restaurant.employeeRosterFiles}
                            onFilesSelected={(files) => updateRestaurant(index, "employeeRosterFiles", files)}
                            description="A spreadsheet is easiest, but a document or simple list is fine. Include staff names and email addresses if you want us to help invite your team."
                            note="You can send your staff list after your menu is set up if you prefer."
                            error={restaurantErrors.employeeRosterFiles}
                            inputId={`ro-employee-roster-${index}`}
                          />
                        </div>
                        <div className="col-md-6">
                          <FileDropInput
                            label="Supporting Documents (optional)"
                            files={restaurant.supportingDocuments}
                            multiple
                            onFilesSelected={(files) => updateRestaurant(index, "supportingDocuments", files)}
                            description="Upload allergy notes, ingredient lists, training guides, menu notes, or anything else that may help us build accurate lessons."
                            error={restaurantErrors.supportingDocuments}
                            inputId={`ro-supporting-documents-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {errors.submit ? <div className="alert alert-danger mt-4 mb-0">{errors.submit}</div> : null}
              </div>
              <div className="card-footer border-0 px-4 pb-4" style={{ background: "transparent" }}>
                <button type="submit" className="homepage-primary-cta" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Send onboarding materials"}
                </button>
                <p style={{ marginTop: "0.75rem", marginBottom: 0, fontSize: "0.875rem", color: "var(--bs-secondary-color)", lineHeight: 1.6 }}>
                  After you submit, we&#8217;ll review your materials, prepare your initial setup, and follow up with next steps.
                </p>
              </div>
            </form>
          )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
